import React, { useState, useEffect } from 'react';
import { db } from './firebase'; 
import { collection, addDoc, doc, onSnapshot, query, setDoc } from 'firebase/firestore';
// CORRECCIÓN: Se importan TODOS los íconos necesarios (Check, Plus, etc.)
import { WifiOff, Activity, Plus, AlertCircle, Users, HelpCircle, ArrowUp, MessageCircle, Check } from 'lucide-react';
import { BRANDING, INITIAL_DATA, formatCurrency } from './utils/helpers';
import ChatBot from './components/ChatBot';
import LoginScreen from './components/LoginScreen';
import BookingForm from './components/BookingForm';
import AdminPanel from './components/AdminPanel';

// CORRECCIÓN: Se cambia el nombre de la función a 'App' para que coincida con el export default y index.js
function App() {
  const [view, setView] = useState('home');
  // const [data, setData] = useState(INITIAL_DATA); // (No se usa 'data' directamente, se usan estados individuales)
  const [user, setUser] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [lastApp, setLastApp] = useState(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  
  const [config, setConfig] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [auth, setAuth] = useState(null); 
  const [appointments, setAppointments] = useState([]);
  const [logs, setLogs] = useState([]);
  const [clinicalRecords, setClinicalRecords] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
      window.addEventListener('online', () => setIsOnline(true));
      window.addEventListener('offline', () => setIsOnline(false));
      const handleScroll = () => setShowScrollBtn(window.scrollY > 300);
      window.addEventListener('scroll', handleScroll);
      
      const unsubConfig = onSnapshot(doc(db, "system", "config"), d => d.exists() ? setConfig(d.data()) : setDoc(doc(db, "system", "config"), INITIAL_DATA.config));
      const unsubFaqs = onSnapshot(doc(db, "system", "faqs"), d => d.exists() ? setFaqs(d.data().list) : setDoc(doc(db, "system", "faqs"), { list: INITIAL_DATA.faqs }));
      const unsubAuth = onSnapshot(doc(db, "system", "auth"), d => d.exists() ? setAuth(d.data()) : setDoc(doc(db, "system", "auth"), INITIAL_DATA.auth));
      const unsubApps = onSnapshot(query(collection(db, "appointments")), s => setAppointments(s.docs.map(d => ({id:d.id, ...d.data()}))));
      const unsubLogs = onSnapshot(query(collection(db, "logs")), s => setLogs(s.docs.map(d => ({id:d.id, ...d.data()}))));
      const unsubRecs = onSnapshot(doc(db, "system", "clinicalRecords"), d => d.exists() && setClinicalRecords(d.data()));
      
      return () => { 
          window.removeEventListener('online', () => setIsOnline(true));
          window.removeEventListener('offline', () => setIsOnline(false));
          window.removeEventListener('scroll', handleScroll);
          unsubConfig(); unsubFaqs(); unsubApps(); unsubLogs(); unsubRecs(); unsubAuth(); 
      };
  }, []);

  const handleBook = async (formWithTime) => {
      const price = formWithTime.tipo === 'Examen' ? 0 : (config?.consultaPriceRef || 0);
      
      const newApp = { 
          ...formWithTime, 
          status: 'Pendiente', 
          priceInRef: price, 
          paymentRate: config?.dolarRate || 0, // Guardamos la tasa del momento
          historyNumber: `HC-${formWithTime.cedula.slice(-4)}`, 
          createdAt: new Date().toISOString() 
      };
      
      try { 
          await addDoc(collection(db, "appointments"), newApp); 
          // Opcional: Log en BD
          // addDoc(collection(db, "logs"), { date: new Date().toISOString(), user: 'SYSTEM', action: 'NEW_BOOKING', detail: `Cita web CI ${formWithTime.cedula}` }); 
          setLastApp(newApp); 
          setView('success'); 
      } catch(e) { 
          console.error(e);
          alert("Error de conexión. Intente nuevamente."); 
      }
  };

  const handleLogin = (role) => {
      setUser(role);
      addDoc(collection(db, "logs"), { date: new Date().toISOString(), user: role.label, action: 'LOGIN', detail: 'Inicio de sesión exitoso' });
  };

  if (!config || !auth) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div></div>;

  if (config.isMaintenanceMode && !user && view !== 'admin') {
      return (<div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 text-center p-10"><Activity size={60} className="text-blue-900 mb-4"/><h1 className="text-3xl font-bold text-gray-800">Mantenimiento</h1><button onClick={() => setView('admin')} className="mt-8 text-xs text-gray-400 underline">Acceso Admin</button></div>);
  }

  return (
    <div className="min-h-screen bg-gray-100 font-sans text-gray-800 relative">
      {!isOnline && <div className="bg-red-600 text-white text-center text-xs p-1 sticky top-0 z-50"><WifiOff size={14}/> MODO OFFLINE</div>}
      
      <header className="bg-white shadow-md p-4 sticky top-0 z-40 no-print flex justify-between items-center">
         <div className="flex items-center space-x-2 cursor-pointer" onClick={() => setView('home')}>{BRANDING.logoIcon}<div><h1 className="text-2xl font-bold leading-none">{BRANDING.appName}</h1><p className="text-[10px] text-gray-500 font-bold uppercase">{BRANDING.doctorName}</p></div></div>
         <div className="flex gap-4"><button onClick={() => setView('home')} className="font-bold text-gray-600">Inicio</button><button onClick={() => setView('admin')} className="font-bold text-blue-800 border border-blue-800 px-3 py-1 rounded">Acceso Personal</button></div>
      </header>

      <main className="container mx-auto p-4 pb-20">
        {view === 'admin' ? (!user ? <LoginScreen authData={auth} onLogin={handleLogin} /> : <AdminPanel config={config} setConfig={setConfig} faqs={faqs} setFaqs={setFaqs} auth={auth} setAuth={setAuth} appointments={appointments} logs={logs} setLogs={setLogs} clinicalRecords={clinicalRecords} setClinicalRecords={setClinicalRecords} user={user} setAdminAuth={setUser} />) 
        : view === 'booking' ? <BookingForm config={config} appointments={appointments} onBook={handleBook} /> 
        : view === 'success' ? (
            <div className="max-w-2xl mx-auto bg-white p-10 rounded-xl shadow-2xl text-center mt-10">
                <Check size={64} className="mx-auto text-green-500 mb-4"/>
                <h2 className="text-3xl font-bold mb-2 text-green-800">¡Solicitud Recibida!</h2>
                <p className="text-gray-600 mb-6">{config.successMessage || "Su cita ha sido pre-agendada. Recuerde confirmar su asistencia."}</p>
                
                {config.whatsapp && (
                    <a href={`https://wa.me/${config.whatsapp}?text=Hola, soy ${lastApp?.nombre} (CI ${lastApp?.cedula}). Acabo de solicitar una cita web y deseo confirmarla.`} target="_blank" rel="noreferrer" className="bg-green-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 mx-auto w-full md:w-fit hover:bg-green-700 transition mb-4">
                        <MessageCircle size={24}/> Confirmar por WhatsApp
                    </a>
                )}
                
                <button onClick={() => setView('home')} className="text-gray-500 underline text-sm mt-4">Volver al Inicio</button>
            </div>
        )
        : (
          <div className="space-y-8 animate-fade-in">
             {/* TARJETA PRINCIPAL DE PRECIOS */}
             <div className={`bg-gradient-to-r from-blue-900 to-blue-700 text-white p-6 rounded-2xl shadow-xl flex flex-col md:flex-row justify-between items-center`}>
                <div className="flex gap-8 text-center md:text-left">
                    <div><p className="text-sm font-bold opacity-80">Tasa BCV</p><p className="text-3xl font-bold">{config.dolarRate} Bs</p><p className="text-[10px]">Actualizado: {config.rateUpdatedAt}</p></div>
                    <div className="w-px bg-white opacity-20"></div>
                    <div><p className="text-sm font-bold opacity-80">Consulta</p><p className="text-3xl font-bold">{formatCurrency(config.consultaPriceRef * config.dolarRate, 'Bs')}</p><p className="text-[10px] opacity-80">Ref: ${config.consultaPriceRef}</p></div>
                </div>
                {config.isBookingEnabled ? <button onClick={() => setView('booking')} className="bg-white text-blue-900 px-8 py-3 rounded-full font-bold shadow-lg mt-4 md:mt-0 hover:scale-105 transition flex items-center gap-2"><Plus/> Solicitar Cita</button> : <button disabled className="bg-gray-400 text-white px-8 py-3 rounded-full font-bold cursor-not-allowed">AGENDA CERRADA</button>}
             </div>

             {/* PASOS */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                {[config.step1, config.step2, config.step3].map((s, i) => (<div key={i} className="bg-white p-6 rounded-xl shadow"><div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 text-blue-800 font-bold">{i+1}</div><p className="text-sm text-gray-600">{s}</p></div>))}
             </div>

             {/* INFO Y BUSCADOR */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-white p-6 rounded-xl shadow border-l-4 border-yellow-400"><h3 className="font-bold flex items-center gap-2 mb-2"><AlertCircle className="text-yellow-500"/> Información Importante</h3><p className="text-gray-600 text-sm whitespace-pre-line">{config.importantInfo}</p></div>
                <div className="bg-white p-6 rounded-xl shadow text-center flex flex-col justify-center">
                    <h3 className="font-bold mb-4">Consultar Estatus</h3>
                    <div className="flex gap-2">
                        <input id="searchCi" type="text" placeholder="Cédula..." className="border p-2 rounded w-full text-center outline-none focus:ring-2 ring-blue-100"/>
                        <button onClick={() => { 
                            const ci = document.getElementById('searchCi').value; 
                            if(!ci) return;
                            const found = appointments.filter(a => a.cedula === ci); 
                            alert(found.length > 0 ? `Hola ${found[0].nombre}. Tienes ${found.length} cita(s). La última está: ${found[found.length-1].status}` : "No encontramos citas con esa cédula."); 
                        }} className="bg-blue-600 text-white p-2 rounded hover:bg-blue-700"><Users/></button>
                    </div>
                </div>
             </div>

             {/* FAQS */}
             <div className="bg-white p-6 rounded-xl shadow"><h3 className="font-bold mb-4 flex gap-2"><HelpCircle/> Preguntas Frecuentes</h3><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{(faqs || []).map(faq => <div key={faq.id} className="border-b pb-2"><p className="font-bold text-xs text-blue-800">{faq.q}</p><p className="text-xs text-gray-500">{faq.a}</p></div>)}</div></div>
          </div>
        )}
        
        {showScrollBtn && <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="fixed bottom-24 right-6 bg-gray-800 text-white p-3 rounded-full shadow-xl hover:bg-gray-700 animate-bounce z-40"><ArrowUp/></button>}
        {!showChat && <button onClick={() => setShowChat(true)} className="fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:bg-blue-700 z-50 flex items-center gap-2 font-bold animate-bounce"><MessageCircle size={24}/> Asistente</button>}
        {showChat && <ChatBot faqs={faqs} onClose={() => setShowChat(false)} />}
      </main>
    </div>
  );
}

// CORRECCIÓN: Se exporta como 'App' por defecto para que index.js lo encuentre
export default App;